/**
 * seoKeywords.js — Comprehensive SEO keyword catalog for Tri-Axis XYZ.
 *
 * Pulled from competitor SERP analysis (gsourcedata, indovance, hitechbim,
 * landdrafters, draftingsources, chemionix, digitiseit, cadtechies) + Google
 * Trends for the CAD-drafting outsourcing industry. Each block is intentionally
 * dense and is consumed by:
 *   - the <Seo> hook (meta keywords + description)
 *   - the JSON-LD Service / LocalBusiness schemas
 *   - state / county / metro / service landing pages
 */

/** Primary global business keywords — referenced on every page. */
export const PRIMARY_KEYWORDS = [
  "CAD drafting services USA",
  "outsource CAD drafting",
  "outsource land survey CAD drafting",
  "AutoCAD drafting services",
  "Civil 3D drafting outsourcing",
  "MicroStation drafting outsourcing",
  "Revit MEP outsourcing",
  "outsource land surveyor CAD drafting",
  "CAD drafting company USA Canada Australia",
  "remote CAD drafting team",
  "land survey CAD technician services",
  "CAD drafting subscription",
  "white-label CAD drafting",
  "CAD drafting hourly rate USA",
];

/** Survey-CAD-specific keyword pool — used by /survey-techies + state pages. */
export const SURVEY_KEYWORDS = [
  "land surveying CAD drafting services",
  "ALTA NSPS land title survey drafting",
  "boundary survey drafting outsourcing",
  "topographic survey CAD drafting",
  "topo survey drafting outsource",
  "subdivision plat drafting services",
  "construction staking drafting",
  "as-built survey CAD drafting",
  "as-built drawings outsource",
  "elevation certificate drafting LOMA LOMR",
  "floodplain certificate drafting",
  "right-of-way drafting ROW",
  "GIS mapping outsourcing",
  "ArcGIS Pro data conversion",
  "site plan drafting services",
  "Civil 3D corridor 3D modeling",
  "DTM DEM drafting services",
  "LiDAR drafting point cloud",
  "drone survey CAD drafting",
  "Trimble Business Center outsourcing",
  "Carlson Survey drafting",
  "survey field-to-finish drafting",
  "PLS-stamp ready drafting",
  "licensed surveyor reviewed CAD",
];

/** CAD-beyond-survey keyword pool — used by /cad-techies + state pages. */
export const CAD_KEYWORDS = [
  "architectural CAD drafting services",
  "permit set drafting",
  "residential architectural drafting",
  "commercial architectural drafting",
  "millwork shop drawings outsource",
  "structural CAD detailing services",
  "rebar detailing BBS",
  "steel detailing AISC CISC",
  "Tekla Structures detailing outsource",
  "precast prefab erection drawings",
  "MEP CAD drafting services",
  "MEP BIM coordination outsourcing",
  "Revit MEP modeling outsource",
  "Navisworks clash detection",
  "scan-to-BIM services",
  "Revit family creation",
  "mechanical CAD drafting services",
  "SolidWorks mechanical drafting outsource",
  "Inventor 3D modeling outsource",
  "sheet metal design drafting",
  "PFD P&ID drafting services",
  "electrical CAD drafting services",
  "single-line diagram drafting",
  "panel layout drafting",
  "lighting design drafting",
  "plumbing piping isometric drawings",
  "piping spool drafting",
  "HVAC duct drafting services",
  "civil engineering drafting outsourcing",
  "road design plan profile drafting",
  "stormwater drainage drafting",
  "3D rendering architectural visualization",
  "photoreal architectural rendering",
  "3D walkthrough animation outsource",
  "VR AR architectural model",
  "PDF to CAD conversion services",
  "paper to CAD redraw",
  "raster to vector CAD conversion",
  "legacy DWG DGN redraw",
  "solar PV design drafting",
  "rooftop solar permit drawings",
  "signage shop drawings drafting",
  "pre-media packaging artwork outsource",
];

/** Keyword pool keyed by service slug — used by per-service meta + structured data. */
export const KEYWORDS_BY_SLUG = {
  "alta-nsps-surveys": [
    "ALTA NSPS survey drafting",
    "ALTA land title survey CAD",
    "ALTA Table-A drafting",
    "ALTA survey for lenders title companies",
  ],
  "boundary-surveys": [
    "boundary survey drafting",
    "legal boundary determination CAD",
    "parcel boundary resolution",
    "monument recovery drafting",
  ],
  "topographic-surveys": [
    "topographic survey drafting",
    "topo CAD drafting outsource",
    "contour mapping 1ft drafting",
    "spot elevation drafting",
  ],
  "subdivision-plats": [
    "subdivision plat drafting",
    "preliminary plat drafting",
    "final plat drafting",
    "re-plat drafting services",
  ],
  "construction-staking": [
    "construction staking drafting",
    "stake-out drawings",
    "cut sheet drafting",
  ],
  "as-built-surveys": [
    "as-built survey drafting",
    "record drawings drafting",
    "post-construction as-built CAD",
  ],
  "floodplain-elevation-certificates": [
    "FEMA elevation certificate drafting",
    "LOMA drafting",
    "LOMR-F drafting",
    "floodplain certificate CAD",
  ],
  "right-of-way": [
    "right-of-way drafting",
    "ROW plan drafting",
    "easement exhibit drafting",
    "DOT ROW outsourcing",
  ],
  "gis-mapping": [
    "GIS mapping outsourcing",
    "shapefile to CAD conversion",
    "geodatabase design",
    "parcel fabric drafting",
  ],
  "site-plans": [
    "site plan drafting",
    "site plan for zoning code",
    "commercial site plan drafting",
  ],
  "3d-modeling": [
    "3D modeling outsource",
    "Civil 3D corridors",
    "BIM-ready terrain modeling",
  ],
  "architectural-cad": ["architectural CAD drafting", "permit drawings drafting"],
  "structural-cad": ["structural CAD detailing", "rebar BBS drafting", "steel shop drawings"],
  "mechanical-cad": ["mechanical CAD drafting", "SolidWorks outsource"],
  "electrical-cad": ["electrical CAD drafting", "single-line diagram drafting"],
  "mep-bim": ["MEP BIM coordination", "Revit MEP outsource", "Navisworks clash detection"],
  "plumbing-piping": ["plumbing drafting", "piping isometric drafting"],
  "hvac-cad": ["HVAC duct drafting", "HVAC equipment schedule drafting"],
  "civil-engineering-drafting": ["civil engineering drafting", "road plan profile"],
  "3d-rendering": ["3D rendering outsource", "architectural visualization"],
  "pdf-to-cad": ["PDF to CAD conversion", "raster to vector"],
  "solar-pv-design": ["solar PV design drafting", "rooftop solar permit drawings"],
  "signage-design": ["signage shop drawings", "pre-media packaging artwork"],
};

/**
 * Compose a comma-separated keyword string for an arbitrary context.
 * @param {string[]} pools — keyword arrays to merge (deduped).
 */
export function composeKeywords(...pools) {
  const seen = new Set();
  pools.flat().forEach((k) => seen.add(k));
  return Array.from(seen).join(", ");
}
